import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { ResponsiveSideDialog } from 'common/components/ResponsiveSideDialog/ResponsiveSideDialog';
import { DialogComponentProps, useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { formatCurrency } from 'common/utils/formatUtils';
import { ButtonRow } from 'components/products/ButtonRow/ButtonRow';
import { useFinancialReserveQuery, useUpdateFinancialReserveMutation } from 'generated/graphql';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DeleteFinancialReserveDialog,
  DeleteFinancialReserveDialogProps,
} from '../DeleteFinancialReserveDialog/DeleteFinancialReserveDialog';
import { DetailRow } from '../DetailRow/DetailRow';
import {
  EditFinancialReserveAmountDialog,
  EditFinancialReserveAmountDialogProps,
} from '../EditFinancialReserveAmountDialog/EditFinancialReserveAmountDialog';
import {
  EditFinancialReserveTextDialog,
  EditFinancialReserveTextDialogProps,
} from '../EditFinancialReserveTextDialog/EditFinancialReserveTextDialog';
import { styles } from './FinancialReserveDetailDialog.styles';

interface FinancialReserveDetailProps {
  id: string;
}

const FinancialReserveDetailDialogContent: React.FC<FinancialReserveDetailDialogProps> = ({ id, onResolve }) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();
  const { isAuthenticated } = useContext(AuthContext);

  const { data, loading: financialReserveLoading } = useFinancialReserveQuery({
    skip: !isAuthenticated,
    variables: {
      id,
    },
  });

  const amount = data?.detail.amount.amount;
  const text = data?.detail.text ?? '';

  const [updateFinancialReserve, { loading: financialReserveUpdateLoading }] = useUpdateFinancialReserveMutation({
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
    },
    refetchQueries: ['FinancialReserves'],
  });

  const loading = financialReserveLoading || financialReserveUpdateLoading;

  const financialReserveAmountDialog = useDialog<EditFinancialReserveAmountDialogProps, number>(
    EditFinancialReserveAmountDialog,
    {
      instanceId: `set-financial-reserve-amount`,
    },
  );

  const handleAmountClick = async () => {
    const newAmount = await financialReserveAmountDialog.create({ amount });
    await updateFinancialReserve({
      variables: {
        id,
        updatedFinancialReserve: {
          amount: { amount: newAmount, currency: 'EUR' },
          text,
        },
      },
    });
  };

  const financialReserveTextDialog = useDialog<EditFinancialReserveTextDialogProps, string>(
    EditFinancialReserveTextDialog,
    {
      instanceId: `set-financial-reserve-text`,
    },
  );

  const handleTextClick = async () => {
    const newText = await financialReserveTextDialog.create({ text });
    await updateFinancialReserve({
      variables: {
        id,
        updatedFinancialReserve: {
          amount: { amount: amount ?? 0, currency: 'EUR' },
          text: newText,
        },
      },
    });
  };

  const deleteFinancialReserveDialog = useDialog<DeleteFinancialReserveDialogProps, boolean>(
    DeleteFinancialReserveDialog,
    {
      instanceId: 'delete-financial-reserve',
    },
  );

  const handleDelete = async () => {
    const deleted = await deleteFinancialReserveDialog.create({ id });
    if (deleted) {
      await onResolve();
    }
  };

  return (
    <Stack>
      <Stack alignItems="center">
        {loading ? (
          <Skeleton height={41} variant="rectangular" />
        ) : (
          <Typography variant="headlineXL">{text}</Typography>
        )}
      </Stack>

      {loading ? (
        <Box mt={3}>
          <Skeleton height={112} variant="rectangular" />
        </Box>
      ) : (
        <Stack sx={styles.container}>
          <DetailRow<number>
            formatter={(value: number) => formatCurrency(value ?? 0, i18n.language)}
            label={t('fintelligenceReport.financialReserveDetailDialog.amount')}
            onClick={handleAmountClick}
            value={amount}
          />
          <DetailRow<string>
            label={t('fintelligenceReport.financialReserveDetailDialog.text')}
            onClick={handleTextClick}
            value={text}
          />
        </Stack>
      )}

      <Box mt={8}>
        {loading ? (
          <Skeleton height={54} variant="rectangular" />
        ) : (
          <ButtonRow
            color="red.dark"
            label={t('fintelligenceReport.deleteFinancialReserveDialog.button')}
            onClick={handleDelete}
          />
        )}
      </Box>
    </Stack>
  );
};

export interface FinancialReserveDetailDialogProps extends FinancialReserveDetailProps, DialogComponentProps<void> {}

export const FinancialReserveDetailDialog: React.FC<FinancialReserveDetailDialogProps> = (props) => {
  return (
    <ResponsiveSideDialog dialogId={props.instanceId} isOpen={props.isOpen} onClose={props.onResolve}>
      <FinancialReserveDetailDialogContent {...props} />
    </ResponsiveSideDialog>
  );
};
